import { useState } from 'react';
import { browserHistory } from '@client/router/compat';
import {
  QueryHookOptions,
  QueryResult,
  WatchQueryFetchPolicy,
} from '@apollo/client';
import auth from '@client/components/auth';
import { RawWorkout } from '@client/new/pages/collection/workouts/workouts.domain';
import { WorkoutsSearchVM } from './workoutTableExtras';

const PAGE_SIZE = 500;

type WorkoutsQueryVariables = { id: number; limit: number; offset: number };

export type WorkoutsPagination = {
  hasPrevPage: boolean;
  hasNextPage: boolean;
  goPrev: () => void;
  goNext: () => void;
};

type WorkoutsPageData = {
  influencerId: number;
  isLoading: boolean;
  creator: { id: number };
  workouts: RawWorkout[];
  refetch: () => Promise<void>;
  pagination: WorkoutsPagination;
};

type UseWorkoutsPageDataParams<TData> = {
  influencerIdParam?: string;
  useQuery: (
    options: QueryHookOptions<TData, WorkoutsQueryVariables>,
  ) => QueryResult<TData, WorkoutsQueryVariables>;
  selectWorkouts: (data: TData | undefined) => RawWorkout[];
  // Applied to the current page after slicing (e.g. scheduled date order).
  sortPage?: (a: RawWorkout, b: RawWorkout) => number;
  // Draft forces 'network-only'; others default to refresh-aware.
  fetchPolicy?: WatchQueryFetchPolicy;
};

// Shared data layer for the flat, cross-program workout list pages: resolves
// the creator id, runs the (offset-paged) query, strips the one-shot `refresh`
// query param, and derives pagination. The Apollo query hook and the field that
// holds the rows differ per list, so both are injected.
export const useWorkoutsPageData = <
  TData extends { creator?: { id: number } | null },
>({
  influencerIdParam,
  useQuery,
  selectWorkouts,
  sortPage,
  fetchPolicy,
}: UseWorkoutsPageDataParams<TData>): WorkoutsPageData => {
  const influencerId = Number(
    influencerIdParam ?? auth.getUser()?.influencerId,
  );

  const [offset, setOffset] = useState(0);

  const url = new URL(window.location.href);
  const shouldRefresh = url.searchParams.get('refresh');

  const {
    loading: isLoading,
    data,
    refetch,
  } = useQuery({
    variables: { id: influencerId, limit: PAGE_SIZE + 1, offset },
    skip: !influencerId,
    fetchPolicy:
      fetchPolicy ?? (shouldRefresh ? 'network-only' : 'cache-first'),
    onCompleted: () => {
      if (shouldRefresh) {
        url.searchParams.delete('refresh');
        browserHistory.replace(url.href);
      }
    },
  });

  // Fetch one extra row to learn whether a further page exists, then drop it.
  const fetched = selectWorkouts(data);
  const hasNextPage = fetched.length > PAGE_SIZE;
  const page = fetched.slice(0, PAGE_SIZE);
  const workouts = sortPage ? page.sort(sortPage) : page;

  return {
    influencerId,
    isLoading,
    creator: data?.creator ?? { id: influencerId },
    workouts,
    refetch: () => refetch().then(() => undefined),
    pagination: {
      hasPrevPage: offset > 0,
      hasNextPage,
      goPrev: () => setOffset((o) => Math.max(0, o - PAGE_SIZE)),
      goNext: () => setOffset((o) => o + PAGE_SIZE),
    },
  };
};

export const useWorkoutsSearch = (): {
  searchText: string;
  search: WorkoutsSearchVM;
} => {
  const [searchText, setSearchText] = useState('');

  return {
    searchText,
    search: {
      value: searchText,
      onChange: setSearchText,
      onClear: () => setSearchText(''),
    },
  };
};
